import { Icon, Stack, spacing, Text } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import { forwardRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import type { Element } from '../../gen/openapi';
import { LayoutNode } from '../components/LayoutNode';
import { WithoutWidgetFocus } from '../WidgetFocusProvider';
import { EXPORT_MIN_HEIGHT, EXPORT_WIDTH } from './renderToFile';

/** Off-screen but laid out: with `display: none` the charts measure nothing and export blank. */
const Stage = styled.div`
  position: fixed;
  top: 0;
  left: -100000px;
  pointer-events: none;
  z-index: -1;
`;

/** The captured sheet: fixed width, height growing with the content. */
const Board = styled.div`
  box-sizing: border-box;
  width: ${EXPORT_WIDTH}px;
  min-height: ${EXPORT_MIN_HEIGHT}px;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.backgroundLevel1};
  color: ${(props) => props.theme.textPrimary};
`;

const Separator = styled.span`
  color: ${(props) => props.theme.textSecondary};
`;

const EXPORTED_AT_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

type DashboardExportBoardProps = {
  title: string;
  caption: string[];
  element: Element;
};

/**
 * A second, export-only rendering of the same `LayoutNode` tree. Capturing the live panel instead
 * would bake in its width and clip everything below its scroll.
 */
export const DashboardExportBoard = forwardRef<HTMLDivElement, DashboardExportBoardProps>(
  ({ title, caption, element }, ref) => {
    const theme = useTheme();
    // Stamped at mount, so it can't tick over between the layout settling and the capture.
    const [exportedAt] = useState(() => EXPORTED_AT_FORMAT.format(new Date()));

    return (
      <Stage aria-hidden="true">
        <Board ref={ref} data-test-dashboard-export-board>
          <Stack
            gap="r24"
            style={{
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              padding: `${spacing.r24} ${spacing.r24} ${spacing.r16}`,
              borderBottom: `1px solid ${theme.border}`,
            }}
          >
            <Stack direction="vertical" gap="r8" style={{ minWidth: 0 }}>
              <Stack gap="r8">
                <Icon name="Dashboard" size="lg" color="textPrimary" />
                <Text variant="Larger" color="textPrimary">
                  {title}
                </Text>
              </Stack>
              {caption.length > 0 ? (
                <Stack gap="r8" style={{ flexWrap: 'wrap' }}>
                  {caption.map((item, index) => (
                    <Text key={item} variant="Basic" color="textSecondary">
                      {index > 0 ? <Separator>· </Separator> : null}
                      {item}
                    </Text>
                  ))}
                </Stack>
              ) : null}
            </Stack>
            <Text variant="Smaller" color="textSecondary">
              Exported {exportedAt}
            </Text>
          </Stack>
          <Box display="flex" flexDirection="column" gap={spacing.r16} flex="1" padding={spacing.r24}>
            <WithoutWidgetFocus>
              <LayoutNode node={element} />
            </WithoutWidgetFocus>
          </Box>
        </Board>
      </Stage>
    );
  },
);

DashboardExportBoard.displayName = 'DashboardExportBoard';
